import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, CreditCard, Settings, Lock, Unlock, XCircle, Globe, Wifi, ShoppingCart, AlertTriangle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Skeleton } from '../../components/ui/Skeleton';
import { useCard, useBlockCard, useUnblockCard, useUpdateCardSettings } from '../../hooks/useCards';
import { useState } from 'react';

const statusConfig: Record<string, { label: string; variant: 'success' | 'warning' | 'error' | 'default' }> = {
  PENDING_ACTIVATION: { label: 'Pendente Ativação', variant: 'warning' },
  ACTIVE: { label: 'Ativo', variant: 'success' },
  BLOCKED: { label: 'Bloqueado', variant: 'error' },
  CANCELLED: { label: 'Cancelado', variant: 'error' },
  EXPIRED: { label: 'Expirado', variant: 'default' },
};

const typeLabels: Record<string, string> = {
  PREPAID: 'Pré-pago',
  CREDIT: 'Crédito',
  DEBIT: 'Débito',
};

export function CardDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [blockReason, setBlockReason] = useState('');

  const { data: card, isLoading } = useCard(id || '');
  const blockMutation = useBlockCard();
  const unblockMutation = useUnblockCard();
  const updateSettingsMutation = useUpdateCardSettings();

  const handleBlock = async () => {
    if (!id) return;
    try {
      await blockMutation.mutateAsync({ cardId: id, reason: blockReason || 'Bloqueio solicitado pelo cliente' });
      setShowBlockConfirm(false);
      setBlockReason('');
    } catch (error) {
      console.error('Erro ao bloquear cartão:', error);
    }
  };

  const handleUnblock = async () => {
    if (!id) return;
    try {
      await unblockMutation.mutateAsync(id);
    } catch (error) {
      console.error('Erro ao desbloquear cartão:', error);
    }
  };

  const handleToggleSetting = async (setting: 'onlinePurchasesEnabled' | 'internationalEnabled' | 'contactlessEnabled') => {
    if (!id || !card) return;
    try {
      await updateSettingsMutation.mutateAsync({
        cardId: id,
        data: { [setting]: !card[setting] },
      });
    } catch (error) {
      console.error('Erro ao atualizar configuração:', error);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!card) {
    return (
      <div className="text-center py-12">
        <CreditCard className="w-12 h-12 text-surface-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-surface-100 mb-2">Cartão não encontrado</h2>
        <Button variant="secondary" onClick={() => navigate('/banking/cards')}>
          Voltar
        </Button>
      </div>
    );
  }

  const status = statusConfig[card.status] || { label: card.status, variant: 'default' as const };
  const isActive = card.status === 'ACTIVE';
  const isBlocked = card.status === 'BLOCKED';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="secondary" size="sm" onClick={() => navigate('/banking/cards')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Detalhes do Cartão</h1>
          <p className="text-surface-400">{card.brand} {typeLabels[card.type]}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Card Visual */}
          <div className={`relative p-6 rounded-2xl bg-gradient-to-br ${
            card.brand === 'VISA' ? 'from-blue-600 to-blue-800' :
            card.brand === 'MASTERCARD' ? 'from-red-600 to-orange-600' :
            'from-purple-600 to-purple-800'
          } text-white shadow-xl`}>
            <div className="flex justify-between items-start mb-8">
              <div>
                <p className="text-sm opacity-80">{typeLabels[card.type]}</p>
                <p className="text-lg font-semibold">{card.brand}</p>
              </div>
              <Badge variant={status.variant}>{status.label}</Badge>
            </div>
            <p className="text-2xl font-mono tracking-wider mb-6">{card.panMasked}</p>
            <div className="flex justify-between items-end">
              <div>
                <p className="text-xs opacity-80">TITULAR</p>
                <p className="font-medium">{card.cardholderName}</p>
              </div>
              <div className="text-right">
                <p className="text-xs opacity-80">VALIDADE</p>
                <p className="font-medium">
                  {String(card.expiryMonth).padStart(2, '0')}/{card.expiryYear}
                </p>
              </div>
            </div>
            {card.isPhysical && (
              <div className="absolute top-4 right-4">
                <Wifi className="w-6 h-6 opacity-60" />
              </div>
            )}
          </div>

          {/* Limits */}
          <Card>
            <CardHeader>
              <CardTitle>Limites</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-surface-400">Limite por Transação</span>
                <span className="text-surface-100 font-medium">{formatCurrency(card.transactionLimit)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-surface-400">Limite Diário</span>
                <span className="text-surface-100 font-medium">{formatCurrency(card.dailyLimit)}</span>
              </div>
              {card.type === 'CREDIT' && card.creditLimit && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-surface-400">Limite de Crédito</span>
                    <span className="text-surface-100 font-medium">{formatCurrency(card.creditLimit)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-surface-400">Limite Disponível</span>
                    <span className="text-green-400 font-medium">{formatCurrency(card.availableLimit || 0)}</span>
                  </div>
                  <div className="w-full bg-surface-700 rounded-full h-2 mt-2">
                    <div
                      className="bg-primary-500 h-2 rounded-full"
                      style={{
                        width: `${((card.availableLimit || 0) / card.creditLimit) * 100}%`,
                      }}
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Configurações
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ShoppingCart className="w-5 h-5 text-surface-400" />
                  <div>
                    <p className="text-surface-100">Compras Online</p>
                    <p className="text-sm text-surface-400">Permitir compras pela internet</p>
                  </div>
                </div>
                <button
                  onClick={() => handleToggleSetting('onlinePurchasesEnabled')}
                  disabled={!isActive || updateSettingsMutation.isPending}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    card.onlinePurchasesEnabled ? 'bg-primary-500' : 'bg-surface-600'
                  } ${!isActive ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      card.onlinePurchasesEnabled ? 'left-7' : 'left-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-surface-400" />
                  <div>
                    <p className="text-surface-100">Compras Internacionais</p>
                    <p className="text-sm text-surface-400">Permitir compras no exterior</p>
                  </div>
                </div>
                <button
                  onClick={() => handleToggleSetting('internationalEnabled')}
                  disabled={!isActive || updateSettingsMutation.isPending}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    card.internationalEnabled ? 'bg-primary-500' : 'bg-surface-600'
                  } ${!isActive ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      card.internationalEnabled ? 'left-7' : 'left-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Wifi className="w-5 h-5 text-surface-400" />
                  <div>
                    <p className="text-surface-100">Pagamento por Aproximação</p>
                    <p className="text-sm text-surface-400">Contactless / NFC</p>
                  </div>
                </div>
                <button
                  onClick={() => handleToggleSetting('contactlessEnabled')}
                  disabled={!isActive || updateSettingsMutation.isPending}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    card.contactlessEnabled ? 'bg-primary-500' : 'bg-surface-600'
                  } ${!isActive ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      card.contactlessEnabled ? 'left-7' : 'left-1'
                    }`}
                  />
                </button>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Ações Rápidas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link to={`/banking/cards/${id}/transactions`}>
                <Button variant="secondary" className="w-full justify-start">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Ver Transações
                </Button>
              </Link>

              {card.type === 'CREDIT' && (
                <Link to={`/banking/cards/${id}/invoices`}>
                  <Button variant="secondary" className="w-full justify-start">
                    <CreditCard className="w-4 h-4 mr-2" />
                    Ver Faturas
                  </Button>
                </Link>
              )}

              {isActive && !showBlockConfirm && (
                <Button
                  variant="secondary"
                  className="w-full justify-start text-yellow-400 hover:text-yellow-300"
                  onClick={() => setShowBlockConfirm(true)}
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Bloquear Cartão
                </Button>
              )}

              {isBlocked && (
                <Button
                  variant="secondary"
                  className="w-full justify-start text-green-400 hover:text-green-300"
                  onClick={handleUnblock}
                  disabled={unblockMutation.isPending}
                >
                  <Unlock className="w-4 h-4 mr-2" />
                  {unblockMutation.isPending ? 'Desbloqueando...' : 'Desbloquear Cartão'}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Block Confirmation */}
          {showBlockConfirm && (
            <Card className="border-yellow-500/30">
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-center gap-2 text-yellow-400">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">Bloquear Cartão</span>
                </div>
                <p className="text-sm text-surface-300">
                  O cartão será bloqueado imediatamente. Você poderá desbloqueá-lo a qualquer momento.
                </p>
                <textarea
                  className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 text-sm"
                  placeholder="Motivo do bloqueio (opcional)"
                  rows={2}
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                />
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="flex-1"
                    onClick={() => setShowBlockConfirm(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    className="flex-1 bg-yellow-600 hover:bg-yellow-700"
                    onClick={handleBlock}
                    disabled={blockMutation.isPending}
                  >
                    {blockMutation.isPending ? 'Bloqueando...' : 'Confirmar'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Card Info */}
          <Card>
            <CardHeader>
              <CardTitle>Informações</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-surface-400">Tipo</span>
                <span className="text-surface-100">{card.isPhysical ? 'Físico' : 'Virtual'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Criado em</span>
                <span className="text-surface-100">
                  {new Date(card.createdAt).toLocaleDateString('pt-BR')}
                </span>
              </div>
              {card.activatedAt && (
                <div className="flex justify-between">
                  <span className="text-surface-400">Ativado em</span>
                  <span className="text-surface-100">
                    {new Date(card.activatedAt).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              )}
              {card.blockedAt && (
                <div className="flex justify-between">
                  <span className="text-surface-400">Bloqueado em</span>
                  <span className="text-surface-100">
                    {new Date(card.blockedAt).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
